<template>
  <v-container>
    <v-card class="elevation-2">
      <v-card-title>Add Positions to Trade</v-card-title>
      <v-progress-linear v-if="loading.value" indeterminate color="primary" />
      <v-card-text>
        <!-- Strategy Dropdown -->
        <v-select v-model="selectedTradeId" :items="strategyOptions" item-title="strategyName" item-value="tradeId"
          label="Select Strategy" required class="mb-2" color="primary" />
  
        <!-- Table with Checkboxes -->
        <v-table>
          <thead class="bg-grey-lighten-3">
            <tr>
              <th class="text-left">
                <v-checkbox color="primary" density="compact" hide-details :model-value="areAllSelected"
                  @update:model-value="toggleSelectAll" />
              </th>
              <th v-for="header in headers" :key="header.value" class="text-left">
                {{ header.text }}
              </th>
            </tr>
          </thead>
  
          <tbody>
            <tr v-for="item in positions" :key="item.optionPositionId" :class="{ 'text-grey': isRowDisabled(item) }">
              <td>
                <v-checkbox color="primary" density="compact" hide-details :disabled="isRowDisabled(item)"
                  :model-value="isSelected(item)" @update:model-value="toggleRowSelection(item)" />
              </td>
              <td>{{ item.stock_code }}</td>
              <td>{{ item.strike_price }}</td>
              <td>{{ item.right }}</td>
              <td>{{ item.action }}</td>
              <td>{{ item.quantity }}</td>
              <td>{{ item.average_price }}</td>
              <td>{{ item.ltp }}</td>
              <td>{{ item.price }}</td>
              <td>{{ formatDate(item.expiry_date) }}</td>
              <td>
                <v-btn color="error" size="small" class="text-caption rounded-pill"
                  @click="removeOrderFromPosition(item.optionPositionId)">
                  Remove
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-table>
  
        <!-- Submit Button -->
        <v-btn color="primary" class="mt-4" :disabled="!selectedTradeId || selectedPositions.length === 0 || submitting"
          @click="submit">
          <v-progress-circular
            v-if="submitting"
            indeterminate
            size="20"
            width="2"
            class="mr-2"
          ></v-progress-circular>
          {{ submitting ? 'Processing...' : 'Add Positions to Trade' }}
        </v-btn>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'

const store = useStore()

const positions = ref([])
const selectedPositions = ref([])
const strategyOptions = computed(() =>
  store.state.trades.tradeList.map(trade => ({
    tradeId: trade.tradeId,
    strategyName: trade.strategyName
  }))
);

const selectedTradeId = ref(null)
const loading = ref(false)
const submitting = ref(false) // Add this line for submit loading state

const headers = [
  { text: 'Stock Code', value: 'stock_code' },
  { text: 'Strike', value: 'strike_price' },
  { text: 'Right', value: 'right' },
  { text: 'Action', value: 'action' },
  { text: 'Qty', value: 'quantity' },
  { text: 'Avg Price', value: 'average_price' },
  { text: 'LTP', value: 'ltp' },
  { text: 'Price', value: 'price' },
  { text: 'Expiry', value: 'expiry_date' }
]

// --- Selection logic ---
const isSelected = (item) =>
  selectedPositions.value.some(p => p.optionPositionId === item.optionPositionId)

const toggleRowSelection = (item) => {
  const index = selectedPositions.value.findIndex(p => p.optionPositionId === item.optionPositionId)
  if (index === -1) {
    selectedPositions.value.push(item)
  } else {
    selectedPositions.value.splice(index, 1)
  }
}

const areAllSelected = computed(() => {
  const enabled = positions.value.filter(item => !isRowDisabled(item))
  return enabled.length > 0 && enabled.every(item => isSelected(item))
})

const toggleSelectAll = (val) => {
  const enabled = positions.value.filter(item => !isRowDisabled(item))
  selectedPositions.value = val ? enabled : []
}

// --- Row disable condition ---
const isRowDisabled = (item) => item.quantity === 0

// --- Date formatter ---
const formatDate = (dateStr) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('en-IN')
}

// --- Load data ---
const loadPositions = async () => {
  loading.value = true
  const res = await store.dispatch('trades/getAllPositionsWithoutStrategy')
  positions.value = res.positionList || []
  console.log('📦 Load Positions API response:', positions.value);
  loading.value = false
}

const removeOrderFromPosition = async (optionPositionsId) => {
  try {
    console.log('📦 remove Order from position in action', optionPositionsId);
    await store.dispatch('trades/removeOrderFromPosition', {
      optionPositionsId: optionPositionsId
    })
    await loadPositions()
  } catch (e) {
    console.error('Error removing order from position:', e)
    alert('Failed to remove order from position.')
  }
}

const submit = async () => {
  submitting.value = true // Start loading
  try {
    await store.dispatch('trades/markStrategy', {
      optionPositionsIdList: selectedPositions.value.map(p => p.optionPositionId),
      tradeId: selectedTradeId.value
    })
    selectedPositions.value = []
    await loadPositions()
  } catch (e) {
    console.error('Error marking strategy:', e)
    alert('Failed to add positions to trade.')
  } finally {
    submitting.value = false // End loading regardless of success/failure
  }
}

onMounted(async () => {
  console.log('onMounted called in manage trades form')
  await Promise.all([loadPositions()])
})
</script>